import { useCallback } from "react";

import { useUpdateMedication } from "./useCalendarData";

/**
 * Store the device she just told the coach she is actually holding.
 *
 * The coach reads `medication.form` to avoid asking a question her calendar has
 * already been told the answer to. When she says the recorded answer is wrong,
 * the record is wrong for every surface that reads it — the factsheet, the
 * inventory unit, the next cycle's walk — and leaving it would make the next
 * question worse rather than better.
 *
 * ### Why this is a hook in `care` and not code in the coach
 *
 * Nothing in the injection-coach package may reach the API except the consent
 * receipt; `no-retention.test.ts` asserts it module by module, and the reason is
 * that the package holds a camera. So the coach reports the correction upward
 * and the surface that knows which record it is performs the write.
 *
 * ### What it refuses
 *
 * A method the record already names, which is a request for nothing. That is
 * the only refusal left: the coach asks its question from the same five values
 * her own form writes, so what arrives here is already a storable answer and
 * there is nothing to translate or to lose.
 *
 * Failure is silent too, and deliberately: she is standing with a syringe, the
 * walk in front of her already has the right answer, and an error toast about
 * her calendar at that moment would be the app asking her to care about its
 * bookkeeping.
 */
export function useMedicationFormCorrection(
  medicationId: string | null | undefined,
  currentApiForm: string | null | undefined,
): (method: string) => void {
  const updateMedication = useUpdateMedication();

  return useCallback(
    (method: string) => {
      if (!medicationId) return;
      const next = method.trim().toLowerCase();
      if (!next || next === currentApiForm?.trim().toLowerCase()) return;

      updateMedication.mutate(
        { medicationId, form: next },
        {
          onError: () => {
            // Nothing. See the note above: the walk is already right, and the
            // record can be corrected again next time she is asked.
          },
        },
      );
    },
    [currentApiForm, medicationId, updateMedication],
  );
}
